'use client';

/**
 * CanadaLandingPage — `/ca/landing-page`.
 *
 * The "StartEngine Canada Homepage" handoff, built on the V3 editorial theme
 * (`components/HomepageV3/theme`) so it stays visually identical to the V3
 * homepage and `/private`:
 *
 *   nav → hero (+ platform stats) → offered-on-StartEngine ticker → featured
 *   spotlight → "Available in Canada" shelf → portfolio-strategy quote →
 *   "Why Invest Pre-IPO" manifesto → how to start → CTA band →
 *   Canadian investor disclosure + Terms + Privacy → footer.
 *
 * The StartEngine mobile app is not offered in Canada, so the CTA band and the
 * footer both drop their app-download columns (`showApp` / `showAppBadges`).
 *
 * Every "Book a Call" CTA opens the existing sales-agent calendar (the same
 * `useCalendarBooking` flow `/private` uses). Offering-dependent content comes
 * from the `canada-landing-pages` Strapi entry via `useCanadaLandingPage`, with
 * the handoff copy as the fallback.
 *
 * This page is distinct from — and does not replace — `/private-canada`.
 */
import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import * as Sentry from 'utils/errorTracking';
import GlobalStyles from '@mui/material/GlobalStyles';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { trackEvent, trackEventWithGTM } from 'utils/analytics';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useCanadaLandingPage from 'hooks/useCanadaLandingPage';
import useCalendarBooking from 'components/Private/hooks/useCalendarBooking';
import { getSalesAgentCalendar } from 'services/user';
import { FALLBACK_CALENDAR_URL, OLD_GENERIC_ROUND_ROBIN } from 'utils/calendarConstants';
import EditorialRoot, {
  editorialScrollGlobalStyles,
} from 'components/HomepageV3/theme/globalStyles';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';
import CanadaNav from './components/CanadaNav';
import CanadaOptOutBar from './components/CanadaOptOutBar';
import { CANADA_OFFERING_ROUTE } from './data/canadaSite';
import PastOfferingsTicker from 'components/HomepageV3/sections/PastOfferingsTicker';
import OfferingSpotlight from 'components/HomepageV3/sections/OfferingSpotlight';
import EditorialQuoteSection from 'components/HomepageV3/sections/EditorialQuoteSection';
import WpManifestoSection from 'components/HomepageV3/sections/WpManifestoSection';
import HowToStartSection from 'components/HomepageV3/sections/HowToStartSection';
import AppDownloadSection from 'components/HomepageV3/sections/AppDownloadSection';
import EditorialFooter from 'components/HomepageV3/sections/EditorialFooter';
import CanadaHero from './sections/CanadaHero';
import CanadaOfferingShelf from './sections/CanadaOfferingShelf';
import CanadaLegalSections from './sections/CanadaLegalSections';
import CanadaFooterLegal from './sections/CanadaFooterLegal';
import {
  CANADA_APP_BAND,
  CANADA_CTA_LABEL,
  CANADA_HOW_TO_LEAD,
  CANADA_HOW_TO_STEPS,
  CANADA_MANIFESTO_COLUMNS,
  CANADA_MANIFESTO_DISCLAIMER,
  CANADA_MANIFESTO_HEADLINE,
  CANADA_QUOTE_DISCLAIMER,
  CANADA_TICKER_DISCLOSURE,
  CANADA_TICKER_FOOTNOTE_MARKER,
} from './data/canadaContent';

const TRACKING_PAGE = 'canada_landing_page';

/**
 * Offering names here end in a series number ("Lambda Labs Canada 1"), which
 * the 44px serif headline would otherwise wrap as a lone trailing digit.
 * Balancing the lines keeps the number with the name it belongs to.
 */
const BalancedSpotlight = styled(OfferingSpotlight)({
  '& .se-spotlight-name': {
    textWrap: 'balance',
  },
});

/* `.se-spotlight-actions a` — secondary link beside the spotlight CTA. */
const SpotlightOfferingLink = styled(Link)({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: COLORS.champagne,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { textDecoration: 'underline' },
});

const captureTrackingError = (e: unknown) =>
  Sentry.captureException(e, { tags: { area: TRACKING_PAGE } });

export default function CanadaLandingPage() {
  const hasTrackedView = useRef(false);
  const router = useRouter();
  const isSignedIn = useIsSignedIn();
  const content = useCanadaLandingPage();

  const { data: calendarData } = useQuery({
    queryKey: ['salesAgentCalendar'],
    queryFn: () => getSalesAgentCalendar(),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
  const isCalendarApiEmpty =
    !calendarData?.calendarUrl ||
    calendarData.calendarUrl === OLD_GENERIC_ROUND_ROBIN ||
    calendarData.calendarUrl === null;
  const calendarUrl = isCalendarApiEmpty ? FALLBACK_CALENDAR_URL : calendarData.calendarUrl;
  const { openCalendar } = useCalendarBooking(calendarUrl);

  useEffect(() => {
    if (hasTrackedView.current || isSignedIn === null) return;
    hasTrackedView.current = true;
    trackEvent('viewed_canada_landing_page', {
      page: TRACKING_PAGE,
      variant: 'se3',
      is_authenticated: isSignedIn,
    }).catch(captureTrackingError);
  }, [isSignedIn]);

  const bookCall = (surface: string) => {
    try {
      trackEventWithGTM('canada_landing_book_a_call_clicked', {
        page: TRACKING_PAGE,
        surface,
        cta: CANADA_CTA_LABEL,
      });
    } catch (e) {
      captureTrackingError(e);
    }
    openCalendar();
  };

  // Offering visuals open the campaign page. Only the explicit "Book a Call"
  // controls open the calendar (Amir QA).
  const viewOffering = (surface: string) => {
    try {
      trackEventWithGTM('canada_landing_view_offering_clicked', {
        page: TRACKING_PAGE,
        surface,
      });
    } catch (e) {
      captureTrackingError(e);
    }
    router.push(CANADA_OFFERING_ROUTE);
  };

  return (
    <>
      <GlobalStyles styles={editorialScrollGlobalStyles} />
      <EditorialRoot data-cy="canada-landing-page">
        <CanadaOptOutBar trackingPage={TRACKING_PAGE} />
        <CanadaNav onCtaClick={() => bookCall('nav')} data-cy="canada-landing-nav" />

        <div data-cy="canada-landing-main">
          <CanadaHero onCtaClick={() => bookCall('hero')} />

          {content.showTicker && (
            <PastOfferingsTicker
              label={null}
              footnoteMarker={CANADA_TICKER_FOOTNOTE_MARKER}
              disclosure={CANADA_TICKER_DISCLOSURE}
              data-cy="canada-past-offerings-ticker"
            />
          )}

          {content.showFeaturedOffering && (
            <BalancedSpotlight
              variant="default"
              spotlight={content.spotlight}
              imageSrc={content.spotlightImage}
              imageAlt={content.spotlight.name}
              isLoadingSpotlight={content.isLoading}
              disclosure={{
                terms: (
                  <>
                    $25,000 minimum USD · Available to eligible investors in Canada. Contact{' '}
                    <a href="mailto:warren@belcopc.com">Belco</a> for offering documents and
                    suitability.
                  </>
                ),
                legal: content.spotlightLegal,
              }}
              secondaryAction={
                <SpotlightOfferingLink
                  href={CANADA_OFFERING_ROUTE}
                  data-cy="canada-spotlight-view-offering"
                >
                  View full offering →
                </SpotlightOfferingLink>
              }
              onCtaClick={() => bookCall('spotlight')}
              onImageClick={() => viewOffering('spotlight_image')}
              data-cy="canada-spotlight"
            />
          )}

          {content.showOfferingShelf && (
            <CanadaOfferingShelf
              shelf={content.shelf}
              card={content.card}
              ctaLabel={CANADA_CTA_LABEL}
              onCtaClick={() => bookCall('offering_card')}
              onViewOffering={() => viewOffering('offering_card')}
            />
          )}

          <EditorialQuoteSection
            disclaimer={CANADA_QUOTE_DISCLAIMER}
            data-cy="canada-quote-section"
          />

          <WpManifestoSection
            headline={CANADA_MANIFESTO_HEADLINE}
            columns={CANADA_MANIFESTO_COLUMNS}
            disclaimer={CANADA_MANIFESTO_DISCLAIMER}
            ctaLabel={CANADA_CTA_LABEL}
            onCtaClick={() => bookCall('manifesto')}
            data-cy="canada-manifesto-section"
          />

          <HowToStartSection
            steps={CANADA_HOW_TO_STEPS}
            lead={CANADA_HOW_TO_LEAD}
            ctaLabel={CANADA_CTA_LABEL}
            trackingPage={TRACKING_PAGE}
            onCtaClick={() => bookCall('how_to_start')}
          />

          <AppDownloadSection
            showApp={false}
            accessText={CANADA_APP_BAND.accessText}
            accessEm={CANADA_APP_BAND.accessEm}
            accessTail={CANADA_APP_BAND.accessTail}
            signupLabel={CANADA_APP_BAND.signupLabel}
            footnote={null}
            trackingPage={TRACKING_PAGE}
            onSignUpClick={() => bookCall('app_download')}
          />

          <CanadaLegalSections showBelcoDisclosure={content.showBelcoDisclosure} />
        </div>

        <EditorialFooter
          showAppBadges={false}
          showBrandRow={false}
          legal={<CanadaFooterLegal />}
          data-cy="canada-landing-footer"
        />
      </EditorialRoot>
    </>
  );
}
